'use client';

import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';
import { ProductMedia } from '@/components/product-media';
import Link from 'next/link';
import { ShoppingCart, Check } from 'lucide-react';
import { AddToCartDialog } from '@/components/add-to-cart-dialog';
import { ProductVariantDialog } from '@/components/product-variant-dialog';
import { formatCurrency } from '@/lib/utils';
import { useProductCardActions, getCatalogEmoji } from '@/hooks/use-product-card-actions';
import { type ProductCardProps } from '@/components/ProductCard';

function FeaturedProductCardComponent({
  product,
  storeId,
  publicStoreId,
  locale,
  isStoreClosed = false,
}: ProductCardProps) {
  const t = useTranslations('product');
  const [imageError, setImageError] = useState(false);

  const {
    isAdding,
    justAdded,
    showDialog,
    setShowDialog,
    showVariantDialog,
    setShowVariantDialog,
    isOutOfStock,
    isInactive,
    isPresale,
    isUnavailable,
    handleAddToCart,
  } = useProductCardActions(product, storeId, isStoreClosed);

  const storePathSegment = publicStoreId ?? storeId;
  const productUrl = `/${locale}/${storePathSegment}/product/${product.id}`;
  const formattedPrice = formatCurrency(product.price, locale);
  const hasPromotion =
    !!product.promotionPrice &&
    product.promotionPrice > 0 &&
    product.price > 0 &&
    product.promotionPrice < product.price;
  const discountPercent = hasPromotion
    ? Math.round(((product.price - product.promotionPrice!) / product.price) * 100)
    : null;

  return (
    <>
      <AddToCartDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        productName={product.name}
        storeId={storeId}
        publicStoreId={storePathSegment}
        locale={locale}
        translations={{
          title: t('addedToCart'),
          goToCart: t('goToCart'),
          continueShopping: t('continueShopping'),
        }}
      />
      <ProductVariantDialog
        open={showVariantDialog}
        onOpenChange={setShowVariantDialog}
        product={product}
        storeId={storeId}
        publicStoreId={storePathSegment}
        locale={locale}
      />
      <Link href={productUrl} prefetch={false} className="block h-full">
        <div className="relative flex items-center overflow-hidden rounded-4xl bg-secondary text-secondary-foreground">
          {/* Image — left */}
          <div className="shrink-0 p-3">
            <div className="relative size-[6.25rem] overflow-hidden rounded-4xl bg-muted">
              {product.brand && (
                <div className="absolute top-1.5 left-1.5 z-10">
                  <Badge variant="default" className="max-w-[4.5rem] truncate text-[10px]">
                    {product.brand}
                  </Badge>
                </div>
              )}

              {product.imageUrl && !imageError ? (
                <ProductMedia
                  src={product.imageUrl}
                  alt={product.name}
                  fill
                  className="object-cover"
                  onError={() => setImageError(true)}
                  sizes="100px"
                  autoPlay
                  loop
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted">
                  <span className="text-2xl">{getCatalogEmoji(product.catalogType)}</span>
                </div>
              )}

              {isPresale && (
                <div className="absolute top-1.5 right-1.5 z-20">
                  <Badge variant="default" className="bg-primary px-1 text-[10px] text-primary-foreground hover:bg-primary">
                    {t('presaleBadge')}
                  </Badge>
                </div>
              )}

              {isUnavailable && !isPresale && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 px-2 text-center">
                  <Badge variant="default" className="text-center text-[10px]">
                    {isOutOfStock && !isInactive ? t('outOfStock') : t('unavailable')}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Content — right */}
          <div className="flex min-w-0 flex-1 flex-col justify-center py-2 pl-0.5 pr-3">
            {hasPromotion ? (
              <div className="mb-1">
                <p className="text-base font-semibold text-green-600">
                  {formatCurrency(product.promotionPrice!, locale)}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-xs text-muted-foreground line-through">
                    {formattedPrice}
                  </p>
                  {discountPercent !== null && discountPercent > 0 && (
                    <Badge className="h-4 border-transparent bg-green-600 px-1 py-0 text-[9px] leading-none text-white hover:bg-green-600">
                      -{discountPercent}%
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <p className="mb-1 text-base font-semibold text-foreground">
                {formattedPrice}
              </p>
            )}

            <h3
              className="mb-1 line-clamp-2 text-sm font-medium leading-snug"
              title={product.name}
            >
              {product.name}
            </h3>

            {(product.volume || product.unit) && (
              <p className="text-xs text-muted-foreground">
                {product.volume} {product.unit}
              </p>
            )}

            {isOutOfStock && (
              <p className="mt-0.5 text-xs font-semibold text-red-600">
                {t('outOfStock')}
              </p>
            )}
          </div>

          {!isUnavailable && (
            <Button
              type="button"
              size="icon"
              className="absolute bottom-2 right-2 z-10 size-10 rounded-full shadow-md"
              disabled={isAdding}
              aria-label={
                justAdded
                  ? t('addedToCart')
                  : isStoreClosed
                    ? t('storeClosed')
                    : isOutOfStock
                      ? t('outOfStock')
                      : isInactive
                        ? t('unavailable')
                        : t('addToCart')
              }
              onClick={handleAddToCart}
            >
              {justAdded ? (
                <Check className="size-5" />
              ) : (
                <ShoppingCart className="size-5" />
              )}
            </Button>
          )}
        </div>
      </Link>
    </>
  );
}

export const FeaturedProductCard = memo(
  FeaturedProductCardComponent,
  (a, b) =>
    a.product === b.product &&
    a.storeId === b.storeId &&
    a.publicStoreId === b.publicStoreId &&
    a.locale === b.locale &&
    a.isStoreClosed === b.isStoreClosed
);
